/**
 * Delete Document Dialog
 * Modal de confirmação de exclusão de documento (produto/imóvel/etc)
 * Always-light theme seguindo padrão do DeleteLeadDialog
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDeleteDatabaseDocument } from '../hooks/useDatabaseDocuments';
import { Icon } from '@/shared/icons'
export interface DeleteDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  onSuccess?: () => void;
}

export const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  onSuccess
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteMutation = useDeleteDatabaseDocument();

  if (!isOpen) return null;

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(documentId);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* confirmação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      <div
        className="relative rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-document-dialog"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#FEE2E2' }}
            >
              <Icon name="alert-triangle" className="w-5 h-5" style={{ color: '#DC2626' }} />
            </div>
            <h2
              className="text-lg font-semibold"
              style={{ color: '#1e293b' }}
            >
              Excluir Documento
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
            disabled={isDeleting}
            data-testid="delete-document-close"
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4" style={{ color: '#1e293b' }}>
            Tem certeza que deseja excluir <strong>{documentName}</strong>?
          </p>

          {/* Warning */}
          <div
            className="p-3 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #FECACA'
            }}
          >
            <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>
                Atenção: Esta ação não pode ser desfeita!
              </p>
              <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
                Todas as informações deste documento serão permanentemente removidas.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{
            backgroundColor: '#f9fafb',
            borderColor: '#e5e7eb'
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors"
            style={{
              color: '#1e293b',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
            disabled={isDeleting}
            data-testid="delete-document-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2"
            style={{ backgroundColor: '#ef4444' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
            }}
            disabled={isDeleting}
            data-testid="delete-document-confirm"
          >
            {isDeleting ? (
              <>
                <div
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                />
                Excluindo...
              </>
            ) : (
              <>
                <Icon name="trash" className="w-4 h-4" />
                Excluir
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
